// app/src/contexts/UrlContext.tsx
import React, {
	createContext,
	useCallback,
	useContext,
	useState,
	useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { authConfigsList } from '../utils/constants/authConfigsList';
import {
	AuthFlow,
	MicrosoftAuthMode,
	MicrosoftAuthType,
	UrlContextProps,
} from '../types';
import { flushAnalytics } from '../services/telemetry/appInsights';
import { verifyCallbackSignature } from '../services/CallbackSignatureService';
import { encodeMicrosoftAuthState } from '../utils/functions/microsoftAuthState';
import { DEFAULT_LANGUAGE, normalizeSupportedLanguage } from '../i18n/supportedLanguages';

//--------------------------------------------------------------------
// Mappings et helpers globaux
//--------------------------------------------------------------------

/**
 * Conversion langue ➜ suffixe de domaine national.
 * Par défaut on utilise « eu » afin de pointer sur le domaine générique.
 */
const languageDomainMap: Record<string, string> = {
	en: 'eu',
	fr: 'fr',
	de: 'de',
	es: 'es',
	it: 'it',
	nl: 'nl',
	pl: 'pl',
	pt: 'pt',
	ro: 'ro',
};

const homeClientPaths = ['/', '/pro', '/part'];

const serviceDomainMapPro: Record<string, string> = {
	en: 'contact-services',
	fr: 'contact-services',
	de: 'kontakt-service',
	es: 'contacto-servicios',
	it: 'contatto-servizi',
	nl: 'contact-services',
	pl: 'kontakt-uslugi',
	pt: 'contacto-servicos',
	ro: 'contact-servicii',
};

const serviceDomainMapPart: Record<string, string> = {
	en: 'contact-services-clients',
	fr: 'contact-services-clients',
	de: 'kontakt-kundenservice',
	es: 'contacto-servicios-clientes',
	it: 'contatti-servizio-clienti',
	nl: 'contact-services-clients',
	pl: 'kontakt-uslugi-klienci',
	pt: 'contacto-servicos-clientes',
	ro: 'contact-servicii-clienti',
};

/**
 * Construit l’URL racine à partir de la variable d’environnement unique
 *   REACT_APP_VPAUTO_BASE_URI="https://integration.vpauto.fr"
 *
 * 1. Retire le TLD déjà présent (ici « .fr »).
 * 2. Pour l’Espagne, remplace "vpauto" par "vp-auto".
 * 3. Ajoute le suffixe national correspondant à la langue.
 *
 * Exemples:
 *   fr ➜ https://integration.vpauto.fr
 *   en ➜ https://integration.vpauto.eu
 *   es ➜ https://integration.vp-auto.es
 */
const getRootUrl = (lang: string): string => {
	const rawBase = process.env.REACT_APP_VPAUTO_BASE_URI ?? '';

	// Supprime le TLD final (ex: ".fr") du host
	//   https://integration.vpauto.fr  ->  https://integration.vpauto
	const baseWithoutTld = rawBase.replace(/\.[a-z]{2,3}(?=\/?$)/i, '');

	// Ajoute le tiret "vp-auto" uniquement pour "es"
	const correctedBase =
		lang === 'es' ? baseWithoutTld.replace(/vpauto/i, 'vp-auto') : baseWithoutTld;

	// Ajoute le suffixe .fr, .es, .eu, ...
	const suffix = languageDomainMap[lang] || 'eu';
	return `${correctedBase}.${suffix}`;
};

//--------------------------------------------------------------------
// Context & Provider
//--------------------------------------------------------------------

const UrlContext = createContext<UrlContextProps | undefined>(undefined);

type VerifiedMicrosoftAuthState = {
	redirectionUrl: string;
	targetPage?: string;
};

export const UrlProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	//------------------------------------------------------------------
	// Langues
	//------------------------------------------------------------------

	const { i18n } = useTranslation();
	// Router
	const navigate = useNavigate();
	const location = useLocation();

	const currentLanguage = i18n.language;
	const [verifiedCallbackState, setVerifiedCallbackState] = useState<VerifiedMicrosoftAuthState | null>(
		null
	);
	const [lastKnownMicrosoftAuthMode, setLastKnownMicrosoftAuthMode] = useState<MicrosoftAuthMode>(
		location.pathname.startsWith('/part') ? 'private' : 'pro'
	);
	/**
	 * Détection de la langue, du callback applicatif et du state Microsoft
	 * à partir des query params.
	 *
	 * - ui_locales = "fr", "fr-FR", "fr FR", etc. ➜ on garde les 2 premières lettres.
	 * - fallback langue = "fr".
	 * - callback = transport applicatif du state encodé en base64.
	 */
	useEffect(() => {
		let cancelled = false;

		const searchParams = new URLSearchParams(window.location.search);

		const uiLocales = searchParams.get('ui_locales');
		const finalLang = normalizeSupportedLanguage(uiLocales, DEFAULT_LANGUAGE);

		if (finalLang !== i18n.language) {
			i18n.changeLanguage(finalLang);
		}

		const rawCallback = searchParams.get('callback');
		const sig = searchParams.get('sig');
		const fallbackRedirectionUri = `${getRootUrl(finalLang)}/authorization/callback`;

		const resolveState = async () => {
			if (!rawCallback || !sig) {
				return;
			}

			try {
				const verification = await verifyCallbackSignature(rawCallback, sig);

				if (verification?.valid && verification.callback) {
					const verifiedState = verification.callback as Partial<VerifiedMicrosoftAuthState>;

					if (!cancelled) {
						setVerifiedCallbackState({
							redirectionUrl: verifiedState.redirectionUrl || fallbackRedirectionUri,
							...(verifiedState.targetPage ? { targetPage: verifiedState.targetPage } : {}),
						});
					}
				}
			} catch (error) {
				console.error('Callback signature verification failed:', error);
			}
		};

		void resolveState();

		return () => {
			cancelled = true;
		};
	}, [i18n, location.pathname, location.search]);

	useEffect(() => {
		if (location.pathname.startsWith('/part')) {
			setLastKnownMicrosoftAuthMode('private');
			return;
		}

		if (location.pathname.startsWith('/pro')) {
			setLastKnownMicrosoftAuthMode('pro');
		}
	}, [location.pathname]);

	//------------------------------------------------------------------
	// Helpers de redirection
	//------------------------------------------------------------------


	const redirectToContactUsPro = useCallback(() => {
		const contactUsUrl = `${getRootUrl(currentLanguage)}/pro/${serviceDomainMapPro[currentLanguage]}`;
		window.open(contactUsUrl, '_blank');
	}, [currentLanguage]);

	const redirectToHomePro = useCallback(() => {
		const contactUsUrl = `${getRootUrl(currentLanguage)}/pro/`;
		window.open(contactUsUrl, '_blank');
	}, [currentLanguage]);

	const redirectToHomePart = useCallback(() => {
		const contactUsUrl = `${getRootUrl(currentLanguage)}/`;
		window.open(contactUsUrl, '_blank');
	}, [currentLanguage]);

	const redirectToContactUsPart = useCallback(() => {
		const contactUsUrl = `${getRootUrl(currentLanguage)}/${serviceDomainMapPart[currentLanguage]}`;
		window.open(contactUsUrl, '_blank');
	}, [currentLanguage]);

	const buildSignupCallbackUrl = useCallback(
		(mode: MicrosoftAuthMode) => {
			const languageToUse = currentLanguage;
			const redirectionUrl =
				verifiedCallbackState?.redirectionUrl ??
				`${getRootUrl(languageToUse)}/authorization/callback`;
			const encodedState = encodeMicrosoftAuthState({
				redirectionUrl,
				...(verifiedCallbackState?.targetPage ? { targetPage: verifiedCallbackState.targetPage } : {}),
				mode,
				type: 'signup',
			});
			const callbackUrl = new URL(redirectionUrl);
			callbackUrl.searchParams.set('state', encodedState);
			callbackUrl.searchParams.set('ui_locales', languageToUse);
			return callbackUrl.toString();
		},
		[currentLanguage, verifiedCallbackState?.redirectionUrl, verifiedCallbackState?.targetPage]
	);

	const redirectToProAfterSignup = useCallback(() => {
		const { policy } = authConfigsList['b2b'];
		const proAfterSignupUrl = buildSignupCallbackUrl('pro');

		const logoutUrl =
			`https://${process.env.REACT_APP_TENANT}.b2clogin.com/` +
			`${process.env.REACT_APP_TENANT}.onmicrosoft.com/${policy}/oauth2/v2.0/logout?` +
			`post_logout_redirect_uri=${encodeURIComponent(proAfterSignupUrl)}`;

		flushAnalytics();
		window.location.href = logoutUrl;
	}, [buildSignupCallbackUrl]);

	const redirectToPartAfterSignup = useCallback(() => {
		const { policy } = authConfigsList['b2c'];
		const partAfterSignupUrl = buildSignupCallbackUrl('private');

		const logoutUrl =
			`https://${process.env.REACT_APP_TENANT}.b2clogin.com/` +
			`${process.env.REACT_APP_TENANT}.onmicrosoft.com/${policy}/oauth2/v2.0/logout?` +
			`post_logout_redirect_uri=${encodeURIComponent(partAfterSignupUrl)}`;

		flushAnalytics();
		window.location.href = logoutUrl;
	}, [buildSignupCallbackUrl]);

	const redirectToMicrosoftAuth = useCallback(
		(
			flow: AuthFlow,
			email: string,
			state?: {
				redirectionUrl?: string;
				targetPage?: string;
				mode?: MicrosoftAuthMode;
				type?: MicrosoftAuthType;
			}
		): void => {
			const { clientId, policy, scope } = authConfigsList[flow];
			const redirectionUrl =
				state?.redirectionUrl ??
				verifiedCallbackState?.redirectionUrl ??
				`${getRootUrl(currentLanguage)}/authorization/callback`;
			const mode = state?.mode ?? lastKnownMicrosoftAuthMode;
			const type = state?.type ?? 'signin';
			const targetPage = state?.targetPage ?? verifiedCallbackState?.targetPage;
			const encodedState = encodeMicrosoftAuthState({
				redirectionUrl,
				...(targetPage ? { targetPage } : {}),
				mode,
				type,
			});

			const loginUrl =
				`https://${process.env.REACT_APP_TENANT}.b2clogin.com/` +
				`${process.env.REACT_APP_TENANT}.onmicrosoft.com/${policy}/oauth2/v2.0/authorize?` +
				`client_id=${clientId}` +
				`&state=${encodeURIComponent(encodedState)}` +
				`&response_type=code` +
				`&redirect_uri=${encodeURIComponent(redirectionUrl)}` +
				`&scope=${encodeURIComponent(scope)}` +
				`&login_hint=${encodeURIComponent(email)}` +
				`&nonce=defaultNonce` +
			`&ui_locales=${encodeURIComponent(currentLanguage)}`;

			const logoutUrl =
				`https://${process.env.REACT_APP_TENANT}.b2clogin.com/` +
				`${process.env.REACT_APP_TENANT}.onmicrosoft.com/${policy}/oauth2/v2.0/logout?` +
				`post_logout_redirect_uri=${encodeURIComponent(loginUrl)}`;

			flushAnalytics();
			window.location.href = logoutUrl;
		},
		[currentLanguage, lastKnownMicrosoftAuthMode, verifiedCallbackState?.redirectionUrl, verifiedCallbackState?.targetPage]
	);

	/**
	 * Bouton retour générique :
	 * - Si on est sur /, /pro ou /part ➜ redirection vers la homepage VPauto (basée sur REACT_APP_VPAUTO_BASE_URI).
	 * - Sinon ➜ navigate(-1) dans l'historique du SPA.
	 */
	const goBackWithFallback = useCallback(() => {
		const path = location.pathname;

		if (homeClientPaths.includes(path)) {
			const homepageUrl = getRootUrl(currentLanguage);
			flushAnalytics();
			window.location.href = homepageUrl;
		} else {
			navigate(-1);
		}
	}, [location.pathname, navigate, currentLanguage]);

	//------------------------------------------------------------------
	// Provider
	//------------------------------------------------------------------

	return (
		<UrlContext.Provider
			value={{
				redirectToContactUsPro,
				redirectToContactUsPart,
				redirectToHomePro,
				redirectToHomePart,
				redirectToProAfterSignup,
				redirectToPartAfterSignup,
				redirectToMicrosoftAuth,
				microsoftAuthState: verifiedCallbackState,
				goBackWithFallback,
			}}
		>
			{children}
		</UrlContext.Provider>
	);
};

//--------------------------------------------------------------------
// Hook utilitaire pour consommer
//--------------------------------------------------------------------

export const useUrlContext = () => {
	const context = useContext(UrlContext);
	if (!context) {
		throw new Error("useUrlContext doit être utilisé à l'intérieur de UrlProvider");
	}
	return context;
};
